import maplibregl from "maplibre-gl";
import { getAllCoords } from "./MapUtils";

// ── Rastervorschau nach der Berechnung (2026-08-12) ──────────────────
// Zeigt die Rasterpunkte des letzten Laufs direkt im Zeichenfenster,
// farblich nach eh skaliert — Davids ursprüngliche Idee aus renderProject
// (unten), nur ohne dessen Nebenwirkungen: kein fitBounds, kein Reset der
// Zeichenwerkzeuge, die Skizze bleibt unangetastet auf der Karte stehen.
//
// Der Kartenstil (MapInit.js) hat keine glyphs-URL, ein Symbol-Layer mit
// text-field würde nichts zeichnen (siehe MapFacadeLabels.js) — die
// Lux-Werte kommen deshalb als Hover-Popup (HTML), nicht als Beschriftung.
//
// Die Punkte hängen als map.__calcGridData am Map-Objekt: baut MapLibre
// den Stil neu auf (Wayback-Regler, Hot-Reload), sind Quelle und Layer
// weg — der styledata-Handler in MapView stellt die Vorschau daraus
// wieder her.
// ─────────────────────────────────────────────────────────────────────

export const renderCalculationGrid = (map, calcPoints) => {
  if (!map) return;
  const points = (calcPoints && calcPoints.length ? calcPoints : map.__calcGridData) || [];
  // Punkte ohne Geometrie oder eh aussortieren — Altbestände vor dem
  // process_geometries-Fix können welche haben.
  const valid = points.filter((p) => p.geometry && typeof p.eh === "number");
  if (!valid.length) return;
  map.__calcGridData = valid;

  // Vorherige Vorschau (samt Hover-Handlern) abräumen, Daten behalten.
  removeCalculationGrid(map, true);

  // Farbskala je Feld normiert (min → blau, max → rot), wie in Davids
  // renderProject — absolute Lux-Werte zeigt das Hover-Popup.
  const ehWerte = valid.map((p) => p.eh);
  const minEh = Math.min(...ehWerte);
  let maxEh = Math.max(...ehWerte);
  if (minEh === maxEh) maxEh = minEh + 1;

  const geojson = {
    type: "FeatureCollection",
    features: valid.map((p) => ({
      type: "Feature",
      geometry: p.geometry,
      properties: { eh: p.eh },
    })),
  };

  map.addSource("calc-grid", { type: "geojson", data: geojson });
  map.addLayer({
    id: "calc-grid",
    type: "circle",
    source: "calc-grid",
    paint: {
      // Mit dem Zoom wachsen, damit das Raster weder bei Feldübersicht
      // klumpt noch beim Reinzoomen zu Stecknadeln schrumpft.
      "circle-radius": ["interpolate", ["linear"], ["zoom"], 15, 2.5, 18, 6, 20, 11],
      "circle-color": [
        "interpolate", ["linear"], ["get", "eh"],
        minEh, "#2563eb",
        minEh + (maxEh - minEh) * 0.5, "#facc15",
        maxEh, "#dc2626",
      ],
      "circle-opacity": 0.85,
      "circle-stroke-color": "#ffffff",
      "circle-stroke-width": 0.5,
    },
  });

  // Lux-Wert beim Überfahren eines Punkts. pointer-events der Zeichnung
  // bleiben unberührt: die Handler hängen am Layer, nicht an der Karte.
  const popup = new maplibregl.Popup({
    closeButton: false,
    closeOnClick: false,
    offset: 8,
  });
  const onMove = (e) => {
    const f = e.features && e.features[0];
    if (!f) return;
    popup
      .setLngLat(e.lngLat)
      .setHTML(`<strong>${Math.round(f.properties.eh)} lx</strong>`)
      .addTo(map);
  };
  const onLeave = () => popup.remove();
  map.on("mousemove", "calc-grid", onMove);
  map.on("mouseleave", "calc-grid", onLeave);
  map.__calcGridCleanup = () => {
    map.off("mousemove", "calc-grid", onMove);
    map.off("mouseleave", "calc-grid", onLeave);
    popup.remove();
  };
  // Gewollt sichtbar — NUR in diesem Zustand darf der styledata-Handler
  // (MapView) die Vorschau nach einem Stil-Neuaufbau wiederherstellen.
  map.__calcGridVisible = true;
};

// keepData=true lässt map.__calcGridData stehen (für Toggle „wieder
// einblenden" und die styledata-Wiederherstellung); false vergisst alles.
export const removeCalculationGrid = (map, keepData = false) => {
  if (!map) return;
  // ZUERST das Flag: das removeLayer unten feuert selbst ein
  // styledata-Event — stünde das Flag noch auf sichtbar, würde der
  // Handler in MapView das Raster im selben Atemzug wieder anlegen und
  // der Ausblenden-Haken wäre wirkungslos (Philips Fund, 13.08.).
  map.__calcGridVisible = false;
  if (map.__calcGridCleanup) {
    map.__calcGridCleanup();
    map.__calcGridCleanup = null;
  }
  if (map.getLayer("calc-grid")) map.removeLayer("calc-grid");
  if (map.getSource("calc-grid")) map.removeSource("calc-grid");
  if (!keepData) map.__calcGridData = null;
};

export const renderProject = (map, project) => {
  if (!map || !project || !project.geometry) return;

  // Helper to safely remove a layer if it exists
  const removeLayer = (id) => {
    if (map.getLayer(id)) map.removeLayer(id);
  };

  // Helper to safely remove a source if it exists
  const removeSource = (id) => {
    if (map.getSource(id)) map.removeSource(id);
  };

  // Remove previous project-related layers and sources
  const projectLayers = [
    "project-fill",
    "project-border",
    "calc-points",
    "directions",
    "light-points",
  ];
  projectLayers.forEach(removeLayer);
  ["project", "calculationpoints", "directions", "lightpoints"].forEach(removeSource);

  // Add project geometry source and layers
  map.addSource("project", { type: "geojson", data: project.geometry });
  map.addLayer({
    id: "project-fill",
    type: "fill",
    source: "project",
    paint: { "fill-color": "green", "fill-opacity": 0.5 },
  });
  map.addLayer({
    id: "project-border",
    type: "line",
    source: "project",
    paint: { "line-color": "white", "line-width": 2 },
  });

  // Calculation points
  const calcPoints = project.calculationpoints || [];
  let minEh = 0, maxEh = 1;
  if (calcPoints.length > 0) {
    minEh = Math.min(...calcPoints.map(cp => cp.eh));
    maxEh = Math.max(...calcPoints.map(cp => cp.eh));
  }
  if (minEh === maxEh) maxEh = minEh + 1;

  const calcPointsGeoJSON = {
    type: "FeatureCollection",
    features: calcPoints.map((cp) => ({
      type: "Feature",
      geometry: cp.geometry,
      properties: { eh: cp.eh },
    })),
  };
  map.addSource("calculationpoints", { type: "geojson", data: calcPointsGeoJSON });
  map.addLayer({
    id: "calc-points",
    type: "circle",
    source: "calculationpoints",
    paint: {
      "circle-radius": 6,
      "circle-color": ["interpolate", ["linear"], ["get", "eh"], minEh, "blue", maxEh, "red"],
      "circle-stroke-color": "black",
      "circle-stroke-width": 1,
    },
  });

  // Directions
  const directionsGeoJSON = {
    type: "FeatureCollection",
    features: (project.directions || []).map((d) => ({
      type: "Feature",
      geometry: d.geometry,
      properties: {},
    })),
  };
  map.addSource("directions", { type: "geojson", data: directionsGeoJSON });
  map.addLayer({
    id: "directions",
    type: "line",
    source: "directions",
    paint: { "line-color": "black", "line-width": 2 },
  });

  // Light points
  const lightPointsGeoJSON = {
    type: "FeatureCollection",
    features: (project.lightpoints || []).map((lp) => ({
      type: "Feature",
      geometry: lp.geometry,
      properties: {},
    })),
  };
  map.addSource("lightpoints", { type: "geojson", data: lightPointsGeoJSON });
  map.addLayer({
    id: "light-points",
    type: "circle",
    source: "lightpoints",
    paint: {
      "circle-radius": 6,
      "circle-color": "white",
      "circle-stroke-color": "black",
      "circle-stroke-width": 2,
    },
  });

  // Buildings (update if source exists)
  const buildingsGeoJSON = {
    type: "FeatureCollection",
    features: (project.buildings || []).map((b) => ({
      type: "Feature",
      geometry: b.geometry,
      properties: {},
    })),
  };
  if (map.getSource("buildings")) {
    map.getSource("buildings").setData(buildingsGeoJSON);
  } else {
    map.addSource("buildings", { type: "geojson", data: buildingsGeoJSON });
    map.addLayer({
      id: "buildings-outline",
      type: "line",
      source: "buildings",
      paint: { "line-color": "red", "line-width": 2 },
    });
  }

  // Fit bounds to project
  const coords = getAllCoords(project.geometry.coordinates);
  const lats = coords.map((c) => c[1]);
  const lngs = coords.map((c) => c[0]);
  if (lats.length && lngs.length) {
    map.fitBounds(
      [
        [Math.min(...lngs), Math.min(...lats)],
        [Math.max(...lngs), Math.max(...lats)],
      ],
      { padding: 200 }
    );
  }
};